// The sizing arithmetic behind the in-browser resize, kept apart from the
// canvas work that surrounds it (image.ts) so it can be measured in process:
// `createImageBitmap` and `<canvas>` have no counterpart in the test runner's
// environment, but choosing the output dimensions is plain arithmetic and is
// where a mistake would actually hurt.

// How far down the longest side of a picked image is taken. A document has to
// occupy roughly 1500-2000 pixels on its long edge to be read reliably, and a
// photograph framed reasonably tightly gives the document most of the frame —
// so this is the smallest setting that still leaves recognition quality alone,
// and it is what keeps a 25 MiB phone photograph from travelling as 25 MiB.
export const MAX_DIMENSION = 1600;

// The JPEG quality the re-encode uses. High enough that the compression
// artefacts stay well below the scale of the glyphs the engine reads.
export const JPEG_QUALITY = 0.85;

export interface Dimensions {
  readonly width: number;
  readonly height: number;
}

// The output size for an image of `source`, scaled down to fit inside a square
// of `maxDimension` and never scaled up: an image already inside the box is
// returned unchanged, so a small picture is not blown up and re-encoded into
// something larger than it started.
//
// Both sides are rounded and floored at one pixel, because a canvas of zero
// width throws rather than producing an empty image.
export function fitWithin(source: Dimensions, maxDimension = MAX_DIMENSION): Dimensions {
  const longest = Math.max(source.width, source.height);
  const scale = Math.min(1, maxDimension / longest);
  return {
    width: Math.max(1, Math.round(source.width * scale)),
    height: Math.max(1, Math.round(source.height * scale)),
  };
}
